import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { EndeavorTypeApi } from '../../services/ApiService';

function EndeavorTypeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // ADDED: For debugging
  
  // UPDATED: Better debugging and logic
  console.log('EndeavorTypeEdit: Current URL:', location.pathname);
  console.log('EndeavorTypeEdit: All URL params:', useParams());
  console.log('EndeavorTypeEdit: Extracted id:', id);
  console.log('EndeavorTypeEdit: Type of id:', typeof id);
  
  const isEditing = Boolean(id && id !== 'create' && id !== 'undefined');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    color: '#4a90e2',
    icon: '',
    defaultDurationDays: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('EndeavorTypeEdit: useEffect triggered');
    console.log('EndeavorTypeEdit: Current location:', location.pathname);
    console.log('EndeavorTypeEdit: URL params - id:', id);
    console.log('EndeavorTypeEdit: isEditing:', isEditing);
    
    // UPDATED: Only load if we have a valid numeric ID
    if (isEditing && id && !isNaN(parseInt(id))) {
      loadEndeavorType();
    } else if (id && id !== 'create') {
      console.warn('EndeavorTypeEdit: Invalid ID detected:', id);
      setError(`Invalid endeavor type ID: ${id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.pathname]);

  const loadEndeavorType = async () => {
    console.log('EndeavorTypeEdit: Loading endeavor type with ID:', id);
    
    // UPDATED: Better validation
    if (!id || id === 'create' || id === 'undefined' || isNaN(parseInt(id))) {
      console.log('EndeavorTypeEdit: Invalid ID for loading:', id);
      setError('Invalid endeavor type ID provided');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      console.log('EndeavorTypeEdit: Making API call to get endeavor type:', id);
      const response = await EndeavorTypeApi.getEndeavorType(id);
      console.log('EndeavorTypeEdit: API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('EndeavorTypeEdit: API error response:', errorText);
        throw new Error(`Failed to load endeavor type: ${errorText}`);
      }
      
      const endeavorType = await response.json();
      console.log('EndeavorTypeEdit: Loaded endeavor type data:', endeavorType);
      
      setFormData({
        name: endeavorType.name || '',
        description: endeavorType.description || '',
        isActive: endeavorType.isActive !== false,
        color: endeavorType.color || '#4a90e2',
        icon: endeavorType.icon || '',
        defaultDurationDays: endeavorType.defaultDurationDays || 30
      });
    } catch (error) {
      console.error('EndeavorTypeEdit: Error loading endeavor type:', error);
      setError(error.message || 'Failed to load endeavor type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('EndeavorTypeEdit: Submitting form data:', formData);
      console.log('EndeavorTypeEdit: Is editing mode:', isEditing);
      
      const response = isEditing 
        ? await EndeavorTypeApi.updateEndeavorType(id, formData)
        : await EndeavorTypeApi.createEndeavorType(formData);

      console.log('EndeavorTypeEdit: Submit response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('EndeavorTypeEdit: Submit error response:', errorData);
        throw new Error(errorData?.message || 'Failed to save endeavor type');
      }

      const result = await response.json();
      console.log('EndeavorTypeEdit: Submit success:', result);

      navigate('/settings/endeavor-types');
    } catch (error) {
      console.error('EndeavorTypeEdit: Error saving endeavor type:', error);
      setError(error.message || 'Failed to save endeavor type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings/endeavor-types');
  };

  if (loading && isEditing) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading endeavor type...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-page-header">
        <div>
          <Link to="/settings/endeavor-types" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
            ← Back to Endeavor Types
          </Link>
          <h2>{isEditing ? `Edit Endeavor Type${id ? ` (ID: ${id})` : ''}` : 'Create Endeavor Type'}</h2>
          
          {/* ADDED: Debug info */}
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#666', 
            marginTop: '0.5rem',
            fontFamily: 'monospace' 
          }}>
            Debug: URL={location.pathname}, ID={id}, isEditing={isEditing.toString()}
          </div>
        </div>
      </div>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Enter endeavor type name (e.g., Course, Workshop, Project)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter endeavor type description"
            rows="4"
          />
        </div>

        <div className="form-group">
          <label htmlFor="color">Color</label>
          <input
            type="color"
            id="color"
            name="color"
            value={formData.color}
            onChange={handleInputChange}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="icon">Icon (emoji or text)</label>
          <input
            type="text"
            id="icon"
            name="icon"
            value={formData.icon}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="📚 or icon name"
            maxLength="10"
          />
        </div>

        <div className="form-group">
          <label htmlFor="defaultDurationDays">Default Duration (days)</label>
          <input
            type="number"
            id="defaultDurationDays"
            name="defaultDurationDays"
            value={formData.defaultDurationDays}
            onChange={handleInputChange}
            disabled={loading}
            min="1"
            max="365"
            placeholder="30"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              disabled={loading}
            />
            Active
          </label>
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button 
            type="submit" 
            disabled={loading || !formData.name.trim()}
            style={{ opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Endeavor Type' : 'Create Endeavor Type')}
          </button>
          
          <button 
            type="button" 
            className="secondary" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EndeavorTypeEdit;